import * as _ from "lodash-es";
import { Moment } from "moment-timezone";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ReportTypeEnum from "../../../models/ReportTypeEnum";
import UserService from "../../../services/UserService";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import CommissionReport from "./CommissionReport/CommissionReport";
import { CustomerProductSalesReport } from "./CustomerProductSalesReport/CustomerProductSalesReport";
import { ProductCustomerSalesReport } from "./ProductCustomerSalesReport/ProductCustomerSalesReport";
import OrdersCreated from "./OrdersCreated/OrdersCreated";
import OrdersDetailReport from "./OrdersDetailReport/OrdersDetailReport";
import ProductSearchesByCustomerReport from "./ProductSearchesByCustomerReport/ProductSearchesByCustomerReport";
import { ReportItemInOrders } from "./ReportItemInOrders/ReportItemInOrders";
import "./Reports.scss";
import ReportSalesByUser from "./ReportSalesByUser/ReportSalesByUser";
import ReportSalesByUserProductDetail from "./ReportSalesByUserProductDetail/ReportSalesByUserProductDetail";
import TotalLoginsReport from "./TotalLoginsReport/TotalLoginsReport";
import { VendorActivityReport } from "./VendorActivityReport/VendorActivityReport";
import { InactiveAccountReport } from "./InactiveAccountReport/InactiveAccountReport";
import { AccountsByDateReport } from "./AccountsByDateReport/AccountsByDateReport";
import { OpenOrdersReport } from "./OpenOrdersReport/OpenOrdersReport";
import IOnGetReportDTO from "../../../models/DTO/IOnGetReportDTO";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import IGetReportDTO from "../../../models/DTO/IGetReportDTO";
import IProcessed_Report from "../../../models/IProcessed_Report";
import { SerialsInOrdersReport } from "./SerialsInOrdersReport/SerialsInOrdersReport";
import { InventoryStockLevelsReport } from "./InventoryStockLevelsReport/InventoryStockLevelsReport";
import { InventoryStockHistoryTransactionReport } from "./InventoryStockHistoryTransactionReport/InventoryStockHistoryTransactionReport";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { CustomReport } from "./CustomReport/CustomReport";
import { InventoryCostBreakdownReport } from "./InventoryCostBreakdownReport/InventoryCostBreakdownReport";
import { CategoryQtySalesReport } from "./CategoryQtySalesReport/CategoryQtySalesReport";
import UiContext from "../../../contexts/UiContext";
import { SingleCategoryQtySalesReport } from "./SingleCategoryQtySalesReport/SingleCategoryQtySalesReport";
import { SocketContext } from "../../../contexts/SocketContext";
import { OpenOrdersWithPartialShipmentsReport } from "./OpenOrdersWithPartialShipmentsReport/OpenOrdersWithPartialShipmentsReport";
import { AccountResaleTaxDocumentReport } from "./AccountResaleTaxDocumentReport/AccountResaleTaxDocumentReport";
import { BackorderReport } from "./BackorderReport/BackorderReport";
import { CustomerNotesReview } from "./CustomerNotesReview/CustomerNotesReview";

type Props = {};

export const Reports: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const params = useParams<{ guid: string }>();

    const getBreadcrumbs = (): IBreadcrumbs[] => {
        return [{ title: "Reports", url: "/reports" }];
    };

    const [navItems, setNavItems] = useState<IBreadcrumbs[]>(getBreadcrumbs());
    const [reportComponent, setReportComponent] = useState<any>(null);
    const [processedReport, setProcessedReport] = useState<IProcessed_Report>();

    const getReport = () => {
        if (params.guid) {
            const payload: IGetReportDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                guid: params.guid,
            };

            socket.emit(EndpointsEnum.ReportGetReport, payload);
        }
    };

    const onGetReport = (data: IOnGetReportDTO) => {
        setProcessedReport(data.report);

        document.title = `Orderwerks >> Reports >> ${data.report.title}`;

        const breadCrumbs = getBreadcrumbs();

        breadCrumbs.push({ url: `/reports/${data.report.guid}`, title: data.report.title, skipTranslate: true });

        setNavItems(breadCrumbs);
    };

    const notify = () => {};

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ReportOnGetReport, fn: onGetReport },
        { endpoint: EndpointsEnum.Notify, fn: notify },
    ];

    useEffect(() => {
        document.title = `Orderwerks >> Reports`;

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getReport();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        setCurrentReport();
    }, [processedReport]);

    const parseDate = (date: Moment | string) => {
        let _date = date;

        if (_.isObject(date)) {
            _date = date.format("YYYY-MM-DD hh:hh:hh");
        } else if (!_.isString(date) || !date.length) {
            return;
        }

        return UserService.parseLastUpdated(_date);
    };

    const itemInOrdersReport = () => {
        return <ReportItemInOrders processedReport={processedReport!} />;
    };

    const openOrderReport = () => {
        return (
            <OpenOrdersReport
                socket={socket}
                notifyFn={uiContext.notifyFn}
                setActivityIndicator={uiContext.setActivityIndicator}
                processedReport={processedReport!}
            />
        );
    };

    const backorderReport = () => {
        return (
            <BackorderReport
                socket={socket}
                notifyFn={uiContext.notifyFn}
                setActivityIndicator={uiContext.setActivityIndicator}
                processedReport={processedReport!}
            />
        );
    };

    const vendorActivityReport = () => {
        return <VendorActivityReport supplierModel={supplierModel} userModel={userModel} processedReport={processedReport!} />;
    };

    const inactiveAccountReport = () => {
        return <InactiveAccountReport processedReport={processedReport!} />;
    };

    const salesByCustomerReport = () => {
        // return (
        //     <ReportSalesByCustomer
        //         userModel={userModel}
        //         supplierModel={supplierModel}
        //         setActivityIndicator={props.setActivityIndicator}
        //         socket={socket}
        //         notifyFn={props.notifyFn}
        //         reportData={state.reportData}
        //     />
        // );
    };

    const accountResaleTaxDocumentReport = () => {
        return <AccountResaleTaxDocumentReport processedReport={processedReport!} />;
    };

    const openOrdersWithPartialShipmentsReport = () => {
        return <OpenOrdersWithPartialShipmentsReport processedReport={processedReport!} />;
    };

    const salesByUserReport = () => {
        return (
            <ReportSalesByUser
                supplierModel={supplierModel}
                vendorModel={vendorModel}
                userModel={userModel}
                processedReport={processedReport!}
            />
        );
    };

    const salesByUserProductDetailReport = () => {
        return (
            <ReportSalesByUserProductDetail
                supplierModel={supplierModel}
                vendorModel={vendorModel}
                userModel={userModel}
                processedReport={processedReport!}
            />
        );
    };

    const ordersDetailReport = () => {
        // if (state.periodType) {
        //     let periodType: any = state.periodType;
        //
        return <OrdersDetailReport processedReport={processedReport!} />;
        // }
    };

    const serialsInOrdersReport = () => {
        return <SerialsInOrdersReport processedReport={processedReport!} />;
    };

    const accountsByDateReport = () => {
        // if (state.periodType) {
        //     let periodType: any = state.periodType;
        //
        return <AccountsByDateReport processedReport={processedReport!} />;
    };

    const totalLoginsReport = () => {
        // if (!userModel || !supplierModel || !state.periodType) {
        //     return null;
        // }
        //
        return <TotalLoginsReport supplierModel={supplierModel} userModel={userModel} processedReport={processedReport!} />;
    };

    const customerProductSalesReport = () => {
        // if (!userModel || !supplierModel || !state.periodType) {
        //     return null;
        // }
        //
        return <CustomerProductSalesReport processedReport={processedReport!} />;
    };

    const productCustomerSalesReport = () => {
        return <ProductCustomerSalesReport processedReport={processedReport!} />;
    };

    const customerProductSearchesReport = () => {
        return <ProductSearchesByCustomerReport processedReport={processedReport!} />;
    };

    const ordersCreatedReport = () => {
        // if (!userModel || !supplierModel || !state.periodType) {
        //     return null;
        // }
        //
        return <OrdersCreated supplierModel={supplierModel} userModel={userModel} socket={socket} processedReport={processedReport!} />;
    };

    const ordersSubmittedReport = () => {
        // if (!userModel || !supplierModel || !state.periodType) {
        //     return null;
        // }
        //
        return <OrdersCreated supplierModel={supplierModel} userModel={userModel} socket={socket} processedReport={processedReport!} />;
    };

    const inventoryStockLevelsReport = () => {
        return <InventoryStockLevelsReport processedReport={processedReport!} />;
    };

    const inventoryStockHistoryTransactionReport = () => {
        return <InventoryStockHistoryTransactionReport processedReport={processedReport!} />;
    };

    const inventoryCostBreakdownReport = () => {
        return <InventoryCostBreakdownReport processedReport={processedReport!} />;
    };

    const categoryQtySalesReport = () => {
        return <CategoryQtySalesReport processedReport={processedReport!} />;
    };

    const singleCategoryQtySalesReport = () => {
        return <SingleCategoryQtySalesReport processedReport={processedReport!} />;
    };

    const customReport = () => {
        return <CustomReport processedReport={processedReport!} />;
    };

    const commissionReport = () => {
        return <CommissionReport processedReport={processedReport!} />;
    };

    const commissionTierReport = () => {
        // if (!userModel || !supplierModel) {
        //     return null;
        // }
        //
        // return (
        //     <CommissionTierReport
        //         supplierModel={supplierModel}
        //         userModel={userModel}
        //         socket={socket}
        //         periodType={state.periodType}
        //         startDate={state.startDate}
        //         endDate={state.endDate}
        //     />
        // );
    };

    const volumeRebateReport = () => {
        // if (!userModel || !supplierModel) {
        //     return null;
        // }
        //
        // return (
        //     <VolumeRebateReport
        //         supplierModel={supplierModel}
        //         userModel={userModel}
        //         socket={socket}
        //         periodType={state.periodType}
        //         startDate={state.startDate}
        //         endDate={state.endDate}
        //     />
        // );
    };

    const coopReport = () => {
        // if (!userModel || !supplierModel) {
        //     return null;
        // }
        //
        // return (
        //     <CoopAdvertisementReport
        //         supplierModel={supplierModel}
        //         userModel={userModel}
        //         socket={socket}
        //         periodType={state.periodType}
        //         startDate={state.startDate}
        //         endDate={state.endDate}
        //     />
        // );
    };

    const spiffReport = () => {
        // if (!userModel || !supplierModel) {
        //     return null;
        // }
        //
        // return (
        //     <SpiffReport
        //         supplierModel={supplierModel}
        //         userModel={userModel}
        //         socket={socket}
        //         periodType={state.periodType}
        //         startDate={state.startDate}
        //         endDate={state.endDate}
        //     />
        // );
    };

    const customerNotesReviewReport = () => {
        return <CustomerNotesReview processedReport={processedReport!} />;
    };

    const setCurrentReport = () => {
        let _reportComponent: any;

        switch (processedReport?.report_type) {
            case ReportTypeEnum.ACTIVITY_REPORT:
                _reportComponent = vendorActivityReport();
                break;

            case ReportTypeEnum.OPEN_ORDER_REPORT:
                _reportComponent = openOrderReport();
                break;

            case ReportTypeEnum.OPEN_ORDERS_WITH_PARTIAL_SHIPMENTS:
                _reportComponent = openOrdersWithPartialShipmentsReport();
                break;

            case ReportTypeEnum.ACCOUNT_RESALE_TAX_DOC_REPORT:
                _reportComponent = accountResaleTaxDocumentReport();
                break;

            case ReportTypeEnum.SALES_BY_USER:
                _reportComponent = salesByUserReport();
                break;

            case ReportTypeEnum.SAMPLE_SALES_BY_USER:
                _reportComponent = salesByUserReport();
                break;

            case ReportTypeEnum.SALES_BY_USER_PRODUCT_DETAIL:
                _reportComponent = salesByUserProductDetailReport();
                break;

            case ReportTypeEnum.COMMISSION_REPORT:
                _reportComponent = commissionReport();
                break;

            case ReportTypeEnum.COMMISSION_TIER_REPORT:
                _reportComponent = commissionTierReport();
                break;

            case ReportTypeEnum.VOLUME_REBATE_REPORT:
                _reportComponent = volumeRebateReport();
                break;

            case ReportTypeEnum.COOP_REPORT:
                _reportComponent = coopReport();
                break;

            case ReportTypeEnum.SPIFF_REPORT:
                _reportComponent = spiffReport();
                break;

            case ReportTypeEnum.ORDERS_DETAIL_REPORT:
                _reportComponent = ordersDetailReport();
                break;

            case ReportTypeEnum.TOTAL_LOGINS_REPORT:
                _reportComponent = totalLoginsReport();
                break;

            case ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER:
                _reportComponent = customerProductSalesReport();
                break;

            case ReportTypeEnum.PRODUCT_CUSTOMER_SALES:
                _reportComponent = productCustomerSalesReport();
                break;

            case ReportTypeEnum.PRODUCT_SEARCHES_BY_CUSTOMER:
                _reportComponent = customerProductSearchesReport();
                break;

            case ReportTypeEnum.ORDERS_CREATED:
                _reportComponent = ordersCreatedReport();
                break;

            case ReportTypeEnum.ORDERS_SUBMITTED:
                _reportComponent = ordersSubmittedReport();
                break;

            case ReportTypeEnum.ITEM_IN_ORDERS_REPORT:
                _reportComponent = itemInOrdersReport();
                break;

            case ReportTypeEnum.INACTIVE_ACCOUNT_REPORT:
                _reportComponent = inactiveAccountReport();
                break;

            case ReportTypeEnum.ACCOUNTS_BY_DATE_REPORT:
                _reportComponent = accountsByDateReport();
                break;

            case ReportTypeEnum.SERIALS_IN_ORDERS_REPORT:
                _reportComponent = serialsInOrdersReport();
                break;

            case ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT:
                _reportComponent = inventoryStockLevelsReport();
                break;

            case ReportTypeEnum.INVENTORY_STOCK_HISTORY_TRANSACTION_REPORT:
                _reportComponent = inventoryStockHistoryTransactionReport();
                break;

            case ReportTypeEnum.CUSTOM:
                _reportComponent = customReport();
                break;

            case ReportTypeEnum.INVENTORY_COST_BREAKDOWN_REPORT:
                _reportComponent = inventoryCostBreakdownReport();
                break;

            case ReportTypeEnum.CATEGORY_QTY_SALES_REPORT:
                _reportComponent = categoryQtySalesReport();
                break;

            case ReportTypeEnum.SINGLE_CATEGORY_QTY_SALES_REPORT:
                _reportComponent = singleCategoryQtySalesReport();
                break;

            case ReportTypeEnum.BACKORDER_REPORT:
                _reportComponent = backorderReport();
                break;

            case ReportTypeEnum.CUSTOMER_NOTES_REVIEW:
                _reportComponent = customerNotesReviewReport();
                break;
        }

        setReportComponent(_reportComponent);
    };

    return (
        <div className="Reports">
            <div className={"d-flex flex-row w-100 justify-content-between"}>
                <Breadcrumbs navItems={navItems} />
                <div>{/*<OwPrintButton toggleMenu={() => { uiContext.toggleSidebar(false) }}/>*/}</div>
            </div>

            <div className="mt-3"></div>

            {reportComponent}
        </div>
    );
};
